import { useCallback, useMemo, useRef } from 'react';
import { api } from '../lib/api';
import { useLoad } from '../lib/hooks';
import type { Anchor, Comment, CommentStatus } from '../lib/types';
import type { CommentDraft } from './CommentInput';

export interface NewComment extends CommentDraft {
  itemId?: string | null;
  versionId?: string | null;
  anchor?: Anchor | Record<string, unknown>;
}

export interface CommentActions {
  create: (input: NewComment) => Promise<Comment>;
  reply: (commentId: string, draft: CommentDraft) => Promise<void>;
  setStatus: (commentId: string, status: CommentStatus) => Promise<void>;
  edit: (commentId: string, body: string) => Promise<void>;
  editReply: (commentId: string, replyId: string, body: string) => Promise<void>;
  remove: (commentId: string) => Promise<void>;
  removeReply: (commentId: string, replyId: string) => Promise<void>;
  markRead: (commentId: string) => void;
  markUnread: (commentId: string) => Promise<void>;
}

/** Comments for a project, either for one review item or the general discussion. */
export function useComments(projectId: string, scope: { itemId?: string; general?: boolean }) {
  const query = scope.itemId ? `?item=${scope.itemId}` : scope.general ? '?general=1' : '';
  const { data, setData, loading, error, reload } = useLoad(() => api.get<{ comments: Comment[] }>(`/api/projects/${projectId}/comments${query}`), [projectId, query]);

  const latest = useRef<Comment[]>([]);
  latest.current = data?.comments ?? [];

  const replace = useCallback(
    (comment: Comment) => {
      setData((current) => {
        if (!current) return current;
        const exists = current.comments.some((c) => c.id === comment.id);
        return { comments: exists ? current.comments.map((c) => (c.id === comment.id ? comment : c)) : [...current.comments, comment] };
      });
    },
    [setData],
  );

  const actions = useMemo<CommentActions>(
    () => ({
      async create(input) {
        const { comment } = await api.post<{ comment: Comment }>(`/api/projects/${projectId}/comments`, { ...input });
        replace(comment);
        return comment;
      },
      async reply(commentId, draft) {
        const { comment } = await api.post<{ comment: Comment }>(`/api/comments/${commentId}/replies`, { ...draft });
        replace(comment);
      },
      async setStatus(commentId, status) {
        const { comment } = await api.patch<{ comment: Comment }>(`/api/comments/${commentId}`, { status });
        replace(comment);
      },
      async edit(commentId, body) {
        const { comment } = await api.patch<{ comment: Comment }>(`/api/comments/${commentId}`, { body });
        replace(comment);
      },
      async editReply(_commentId, replyId, body) {
        const { comment } = await api.patch<{ comment: Comment }>(`/api/comments/${replyId}`, { body });
        replace(comment);
      },
      async remove(commentId) {
        await api.del(`/api/comments/${commentId}`);
        setData((current) => current && { comments: current.comments.filter((c) => c.id !== commentId) });
      },
      async removeReply(commentId, replyId) {
        await api.del(`/api/comments/${replyId}`);
        setData((current) => current && { comments: current.comments.map((c) => (c.id === commentId ? { ...c, replies: c.replies.filter((r) => r.id !== replyId) } : c)) });
      },
      markRead(commentId) {
        if (!latest.current.find((c) => c.id === commentId)?.unread) return;
        void api.post(`/api/comments/${commentId}/read`).catch(() => undefined);
        setData((current) => current && { comments: current.comments.map((c) => (c.id === commentId ? { ...c, unread: false } : c)) });
      },
      async markUnread(commentId) {
        await api.post(`/api/comments/${commentId}/unread`);
        setData((current) => current && { comments: current.comments.map((c) => (c.id === commentId ? { ...c, unread: true } : c)) });
      },
    }),
    [projectId, replace, setData],
  );

  return { comments: data?.comments ?? [], loading, error, reload, actions };
}
